import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mediaService, MediaFile } from "../services/firebase";
import "./Gallery.css";
import PrimaryButton from "components/PrimaryButton";

const Gallery: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [galleryImages, setGalleryImages] = useState<MediaFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<MediaFile | null>(null);

  // מציאת אינדקס התמונה הנוכחית
  const currentImageIndex = useMemo(() => {
    if (!selectedImage) return -1;
    return galleryImages.findIndex((img) => img.id === selectedImage.id);
  }, [selectedImage, galleryImages]);

  const closeModal = () => {
    setSelectedImage(null);
  };

  const goToPrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentImageIndex > 0) {
      setSelectedImage(galleryImages[currentImageIndex - 1]);
    }
  };

  const goToNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentImageIndex < galleryImages.length - 1) {
      setSelectedImage(galleryImages[currentImageIndex + 1]);
    }
  };

  useEffect(() => {
    fetchGalleryImages();
  }, []);

  // סגירת פופ-אפ עם Escape וניווט עם חצים
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedImage) return;

      if (e.key === "Escape") {
        closeModal();
      } else if (e.key === "ArrowLeft") {
        if (i18n.language === "he") {
          if (currentImageIndex < galleryImages.length - 1) {
            setSelectedImage(galleryImages[currentImageIndex + 1]);
          }
        } else {
          if (currentImageIndex > 0) {
            setSelectedImage(galleryImages[currentImageIndex - 1]);
          }
        }
      } else if (e.key === "ArrowRight") {
        if (i18n.language === "he") {
          if (currentImageIndex > 0) {
            setSelectedImage(galleryImages[currentImageIndex - 1]);
          }
        } else {
          if (currentImageIndex < galleryImages.length - 1) {
            setSelectedImage(galleryImages[currentImageIndex + 1]);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, currentImageIndex, galleryImages, i18n.language]);

  // מניעת גלילה בגוף הדף כשהפופ-אפ פתוח
  useEffect(() => {
    if (selectedImage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedImage]);

  const fetchGalleryImages = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const images = await mediaService.getAll();
      // סינון רק תמונות גלריה (קבצים עם סיומת תמונה וקטגוריית גלריה או ללא קטגוריה) ולקיחת 6 האחרונות
      const imageFiles = images
        .filter(
          (file) =>
            file.type.startsWith("image/") &&
            (file.category === "gallery" || !file.category)
        )
        .slice(0, 6); // רק 6 התמונות האחרונות
      setGalleryImages(imageFiles);
    } catch (error) {
            setError("שגיאה בטעינת התמונות");
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = (image: MediaFile) => {
    setSelectedImage(image);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const isFirstImage = currentImageIndex === 0;
  const isLastImage = currentImageIndex === galleryImages.length - 1;
  const isRTL = i18n.language === "he";

  if (isLoading) {
    return (
      <div className="container-gallery">
        <div className="header-gallery">
          <h1 className="titleGallery">{t("gallery")}</h1>
          <p className="subtitleBolt">{t("galleryDescriptionBolt")}</p>
          <p className="subtitle">{t("galleryDescription")}</p>
        </div>
        <div className="loading-message">{t("loadingImages")}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-gallery">
        <div className="header-gallery">
          <h1 className="titleGallery">{t("gallery")}</h1>
          <p className="subtitleBolt">{t("galleryDescriptionBolt")}</p>
          <p className="subtitle">{t("galleryDescription")}</p>
        </div>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (galleryImages.length === 0) {
    return (
      <div className="container-gallery">
        <div className="header-gallery">
          <h1 className="titleGallery">{t("gallery")}</h1>
          <p className="subtitleBolt">{t("galleryDescriptionBolt")}</p>
          <p className="subtitle">{t("galleryDescription")}</p>
        </div>
        <div className="empty-gallery">
          <p>אין תמונות זמינות כרגע</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-gallery">
      <div className="header-gallery">
        <h1 className="titleGallery">{t("gallery")}</h1>
        <p className="subtitleBolt">{t("galleryDescriptionBolt")}</p>
        <p className="subtitle">{t("galleryDescription")}</p>
      </div>

      <div className="gallery-grid">
        {galleryImages.map((image) => (
          <div
            className="gallery-item"
            key={image.id}
            onClick={() => openModal(image)}
          >
            <img
              src={image.url}
              alt={image.name}
              className="gallery-image"
              onError={(e) => {
                // אם התמונה לא נטענת, הצג תמונת ברירת מחדל
                const target = e.target as HTMLImageElement;
                target.src = "/images/image-error.jpg";
                target.onerror = null; // מניעת לולאה אינסופית
              }}
            />
          </div>
        ))}
      </div>
      <div className="more-images-button">
        <PrimaryButton onClick={() => navigate("/gallery-detail")}>
          {t("allGallery")}
        </PrimaryButton>
      </div>

      {/* Image Modal - מועבר ל-body באמצעות Portal */}
      {selectedImage &&
        createPortal(
          <div
            className="image-modal-overlay"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-label="תצוגה מוגדלת של תמונה"
          >
            <div className="image-modal-content">
              <button
                className="image-modal-close"
                onClick={closeModal}
                aria-label="סגור"
              >
                ×
              </button>

              <img
                src={selectedImage.url}
                alt={selectedImage.name}
                className="image-modal-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = "/images/image-error.jpg";
                  target.onerror = null;
                }}
              />

              {/* חצים בתחתית במקום קבוע */}
              {galleryImages.length > 1 && (
                <div className="image-modal-nav-container">
                  {/* חץ קודם - תמיד מוצג */}
                  <button
                    className={`image-modal-nav image-modal-prev ${
                      isRTL ? "rtl-nav" : ""
                    }`}
                    onClick={goToPrevious}
                    disabled={isFirstImage}
                    aria-label={isRTL ? "תמונה הבאה" : "תמונה קודמת"}
                    aria-disabled={isFirstImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d={isRTL ? "M15 18L9 12L15 6" : "M9 18L15 12L9 6"}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>

                  {/* אינדיקטור תמונה נוכחית */}
                  <div className="image-modal-counter">
                    {currentImageIndex + 1} / {galleryImages.length}
                  </div>

                  {/* חץ הבא - תמיד מוצג */}
                  <button
                    className={`image-modal-nav image-modal-next ${
                      isRTL ? "rtl-nav" : ""
                    }`}
                    onClick={goToNext}
                    disabled={isLastImage}
                    aria-label={isRTL ? "תמונה קודמת" : "תמונה הבאה"}
                    aria-disabled={isLastImage}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d={isRTL ? "M9 18L15 12L9 6" : "M15 18L9 12L15 6"}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default Gallery;
